import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ChevronLeft, ChevronRight, Download } from 'lucide-react'
import { toast } from 'sonner'

import { Alert, AlertDescription, AlertTitle } from '@quebon/ui/components/alert'
import { Badge } from '@quebon/ui/components/badge'
import { Button } from '@quebon/ui/components/button'
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@quebon/ui/components/card'
import { Spinner } from '@quebon/ui/components/spinner'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@quebon/ui/components/table'
import { cn } from '@quebon/ui/lib/utils'

import { saveBlob } from '@/api/csvDownload'
import {
  LC_LEDGER_CHANNEL_LABELS,
  LC_LEDGER_ENTRY_TYPE_LABELS,
  lcSettlementApi,
  type LcLedgerEntryRow,
  type LcSettlementParams,
} from '@/api/lcSettlement'
import { describeApiError, formatKstDateTime, formatWon } from './format'
import { lcSettlementQueryKeys } from './queryKeys'

interface Props {
  params: LcSettlementParams
}

const PAGE_SIZE = 50

function paramsKey(params: LcSettlementParams): string {
  return JSON.stringify(params)
}

function AmountCell({ value, refund }: { value: number; refund: boolean }) {
  return (
    <TableCell className={cn('text-right tabular-nums', refund && 'text-muted-foreground')}>
      {formatWon(value)}
    </TableCell>
  )
}

function EntryRow({ entry }: { entry: LcLedgerEntryRow }) {
  const refund = entry.entryType === 'REFUND'
  return (
    <TableRow className={cn(refund && 'bg-muted/40 text-muted-foreground')}>
      <TableCell className="tabular-nums">{formatKstDateTime(entry.entryDate)}</TableCell>
      <TableCell>
        <Badge variant={refund ? 'outline' : 'secondary'}>{LC_LEDGER_ENTRY_TYPE_LABELS[entry.entryType]}</Badge>
      </TableCell>
      <TableCell>{LC_LEDGER_CHANNEL_LABELS[entry.channel]}</TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <span className={cn('font-medium', !refund && 'text-foreground')}>{entry.centerName}</span>
          {entry.isTestCenter ? <Badge variant="outline">테스트센터</Badge> : null}
        </div>
        {entry.subdomain ? <div className="text-xs text-muted-foreground">{entry.subdomain}</div> : null}
      </TableCell>
      <TableCell className="font-mono text-xs">{entry.reference}</TableCell>
      <TableCell className="max-w-[320px] truncate" title={entry.description}>{entry.description}</TableCell>
      <TableCell>{entry.beneficiaryName ?? '-'}</TableCell>
      <AmountCell value={entry.totalAmount} refund={refund} />
      <AmountCell value={entry.textbookAmount} refund={refund} />
      <AmountCell value={entry.nonTextbookAmount} refund={refund} />
      <AmountCell value={entry.shippingFee} refund={refund} />
      <TableCell>{entry.provider ?? '-'}</TableCell>
      <TableCell className="font-mono text-xs">{entry.cardLast4 ?? '-'}</TableCell>
      <TableCell className="font-mono text-xs">{entry.pgTransactionId ?? '-'}</TableCell>
      <TableCell className="max-w-[200px] truncate" title={entry.refundReason ?? undefined}>
        {entry.refundReason ?? '-'}
      </TableCell>
    </TableRow>
  )
}

export function EntriesTab({ params }: Props) {
  const [page, setPage] = useState(0)
  const [downloading, setDownloading] = useState(false)
  const key = paramsKey(params)

  // New filter conditions always start from the first page.
  useEffect(() => {
    setPage(0)
  }, [key])

  const entriesQuery = useQuery({
    queryKey: lcSettlementQueryKeys.entries({ ...params, page, size: PAGE_SIZE }),
    queryFn: () => lcSettlementApi.getEntries({ ...params, page, size: PAGE_SIZE }),
    placeholderData: (previous) => previous,
  })
  const pageData = entriesQuery.data
  const entries = pageData?.content ?? []
  const totalPages = pageData?.totalPages ?? 0
  const totalElements = pageData?.totalElements ?? 0

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const { blob, filename } = await lcSettlementApi.exportEntries(params)
      saveBlob(blob, filename)
    } catch (error) {
      toast.error(describeApiError(error, '상세 CSV 다운로드에 실패했습니다.'))
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle>결제 대장 상세</CardTitle>
        <CardDescription>
          {params.dateFrom} ~ {params.dateTo} 기간의 결제·환불 항목입니다. 환불은 환불일에 음수 행으로 표시됩니다. CSV는 페이지가 아니라 조건 전체를 내려받습니다.
        </CardDescription>
        <CardAction>
          <Button type="button" variant="outline" onClick={handleDownload} disabled={downloading || entriesQuery.isLoading}>
            {downloading ? <Spinner data-icon="inline-start" /> : <Download data-icon="inline-start" />}
            CSV 다운로드
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 pt-4">
        {entriesQuery.isError ? (
          <Alert variant="destructive">
            <AlertTitle>항목을 불러오지 못했습니다.</AlertTitle>
            <AlertDescription>{describeApiError(entriesQuery.error, '잠시 후 다시 시도하세요.')}</AlertDescription>
          </Alert>
        ) : entriesQuery.isLoading ? (
          <div className="flex min-h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Spinner />
            항목을 불러오는 중입니다.
          </div>
        ) : (
          <>
            <Table className={cn('min-w-[1600px]', entriesQuery.isFetching && 'opacity-70')}>
              <TableHeader>
                <TableRow>
                  <TableHead>일시</TableHead>
                  <TableHead>구분</TableHead>
                  <TableHead>채널</TableHead>
                  <TableHead>센터</TableHead>
                  <TableHead>참조번호</TableHead>
                  <TableHead>내역</TableHead>
                  <TableHead>원생</TableHead>
                  <TableHead className="text-right">총액 (원)</TableHead>
                  <TableHead className="text-right">교재 (원)</TableHead>
                  <TableHead className="text-right">교재 외 (원)</TableHead>
                  <TableHead className="text-right">배송비 (원)</TableHead>
                  <TableHead>결제수단</TableHead>
                  <TableHead>카드 뒷4자리</TableHead>
                  <TableHead>PG 거래ID</TableHead>
                  <TableHead>환불 사유</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={15} className="py-12 text-center text-muted-foreground">
                      조건에 맞는 결제 항목이 없습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((entry) => <EntryRow key={entry.entryId} entry={entry} />)
                )}
              </TableBody>
            </Table>
            {totalPages > 1 ? (
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  전체 {formatWon(totalElements)}건 · {page + 1} / {totalPages} 페이지
                </span>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={page === 0 || entriesQuery.isFetching}
                    onClick={() => setPage((current) => Math.max(0, current - 1))}
                  >
                    <ChevronLeft data-icon="inline-start" />
                    이전
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages - 1 || entriesQuery.isFetching}
                    onClick={() => setPage((current) => Math.min(totalPages - 1, current + 1))}
                  >
                    다음
                    <ChevronRight data-icon="inline-end" />
                  </Button>
                </div>
              </div>
            ) : totalElements > 0 ? (
              <div className="text-sm text-muted-foreground">전체 {formatWon(totalElements)}건</div>
            ) : null}
          </>
        )}
      </CardContent>
    </Card>
  )
}
